import { Box, Grid, Link, Typography } from '@quillbot/design-system';
import React from 'react';
import { FaqSectionAccorion } from './Faq';

const Content = ({
  first,
  bold,
  children,
  component,
}: {
  first?: boolean;
  component?: 'p' | 'li' | 'ol' | 'ul';
  bold?: boolean;
  children: React.ReactNode;
}) => (
  <Typography
    component={component || 'p'}
    fontSize="16px"
    fontWeight={bold ? 600 : 400}
    pt={first ? 0 : '8px'}
  >
    {children}
  </Typography>
);

const basics: {
  question: string;
  questionComponent: keyof JSX.IntrinsicElements;
  answer: JSX.Element;
}[] = [
  {
    question: 'What is a Citation?',
    questionComponent: 'h3',
    answer: (
      <>
        <Content first>
          A citation is a formal way to acknowledge a source that you used or learned from in some
          way that helped you create a piece of content.
        </Content>

        <Content>There are two types of citations: full and in-text citations.</Content>

        <Content>
          A list of full citations is given on a:
          <Content component="ul">
            <Content component="li" first>
              References page (APA format, Author-Date Chicago style)
            </Content>

            <Content component="li">
              Works Cited page (MLA format, Author-Date Chicago style)
            </Content>

            <Content component="li">
              Bibliography page (Notes and Bibliography Chicago style)
            </Content>
          </Content>
        </Content>

        <Content>
          These pages contain all of the important information necessary for someone else to find
          the material you referenced to create your argument or express your unique ideas.
        </Content>

        <Content>
          Names of authors, titles, publishers, and more are included to make it easy for your
          reader to follow up an idea or point that you showed them when you cited another author’s
          work within your own.
        </Content>

        <Content>
          The formatting of what informational elements are listed when is based on what citation
          style you’re using, and the conventions are different not only between citation styles but
          also between different types of sources.
        </Content>

        <Content>
          In-text citations are the condensed forms of full citations, and they are used within the
          body of your written work. They are placed at any point in a sentence, in parentheses,
          when you bring up an idea or piece of information that you didn’t come up with yourself or
          know before you encountered the source.
        </Content>

        <Content>
          In-text citations often contain the author’s last name, the year the source was published,
          the title of the article you’re referencing, and/or other information relevant to the type
          of source that is being cited at that moment.
        </Content>

        <Content>
          Books, websites, and other kinds of reference material have different conventions for what
          is listed in the parenthetical in-text citation, and this also varies depending on what
          citation style you’re working within.
        </Content>
      </>
    ),
  },
  {
    question: 'Why are Citations Important?',
    questionComponent: 'h3',
    answer: (
      <>
        <Content first>
          Citations are important and worth your time to learn and use for many reasons:
        </Content>

        <Content component="ol">
          <Content component="li" first>
            Citations make it easy for others to locate a source you referenced that interests them.
            In this way, it helps you facilitate more engagement within your reading or research
            community.
          </Content>

          <Content component="li">
            They properly credit others for the ideas that inspired or helped you when writing.
          </Content>

          <Content component="li">
            You build credibility for your ideas and arguments by showing there is outside support
            and or interest in your topic.
          </Content>

          <Content component="li">
            Using citations correctly lowers your chances of unintentionally plagiarizing another
            author, which can have extreme and lasting negative consequences.
          </Content>

          <Content component="li">
            Citations also show that you are well-versed in the subject matter and have done the
            work necessary to understand the research that’s already been done, plus how you can
            thoughtfully add to it in your own unique way.
          </Content>
        </Content>
      </>
    ),
  },
  {
    question: 'The Most Important Citation Rules',
    questionComponent: 'h3',
    answer: (
      <>
        <Content first>
          Nitty-gritty citation rules vary between formatting styles, but here are a few
          overarching, big-picture rules that are important to citing sources successfully and not
          hating your life while doing so:
        </Content>

        <Content component="ul">
          <Content component="li" first bold>
            {/* 1 */}
            Keep track of what information comes from each source.
            <Content>
              You don’t want to have to backtrack and re-read several sources just to figure out
              where you got a fact or idea. Trust us. It’s not a good time.
            </Content>
          </Content>

          <Content component="li" bold>
            {/* 2 */}
            Keep track of what information you take and from where.
            <Content>
              If you’re citing a book, you’ll usually need to know which specific page number(s) you
              took information from for most citation styles.
            </Content>
            <Content>
              Again, save yourself the heartbreak (and perhaps, heartburn) of re-reading each page
              to locate information instead of taking good notes from the start.
            </Content>
          </Content>

          <Content component="li" bold>
            {/* 3 */}
            Understand the citation style in which you’re working.
            <Content>
              No, really. Read the manual, consult our&nbsp;
              <Link
                href="https://quillbot.com/blog/how-to-cite-sources-apa-mla-and-chicago/"
                target="_blank"
              >
                <a>QuillBot citation guides</a>
              </Link>
              , check your in-text and full citations using a citation generator—make sure you get
              it!
            </Content>
            <Content>
              Ensure your citations are correct and any materials or tools you’re using for support
              are up-to-date with the latest editions of the manual for the citation style you’re
              using.
            </Content>
          </Content>
        </Content>
      </>
    ),
  },
  {
    question: 'Citations vs. References',
    questionComponent: 'h3',
    answer: (
      <>
        <Content first>
          The difference between a citation and a reference can be tricky because different styles
          use different words to describe citations and other related elements.
        </Content>

        <Content>
          Unfortunately, there is more than one type of citation and also several ways that the word
          “references” is used when talking about citing or reporting sources. Let’s break them each
          down to clear things up for you.
        </Content>

        <Content>
          As we discussed previously, there are two types of citations: full and in-text citations.
        </Content>

        <Content>
          Full citations are listed on a page that comes at the end of your paper, and they contain
          everything someone would need to find a particular source.
        </Content>

        <Content>
          In-text citations are parenthetical, condensed versions of the full citation that are
          found within your work whenever you discuss the ideas or information you pulled from a
          source.
        </Content>

        <Content>
          A reference is a term used in APA format when talking about a full citation. So, a full
          citation in APA format is a reference, and the terms are used interchangeably if you’re
          working in that style. Calling full citations “references” is shorter and easier, though,
          so it’s usually preferred.
        </Content>

        <Content>
          In the Author-Date system of Chicago style, you may also hear another term that has
          “reference” in it because full citations in this style system are often called “reference
          list entries”.
        </Content>

        <Content>
          For both APA format and the Author-Date system of Chicago style, the list of full
          citations/references/reference list entries are contained on a special page at the end of
          your work that is always titled “References” in APA format and can sometimes be titled
          this for Author-Date Chicago style.
        </Content>

        <Content>
          The word “reference” might also pop up when you’re planning, writing, or discussing your
          work. But, when it’s a verb, “to reference” means only “to mention, refer to, or to cite”.
          It might look the same as a reference that is a noun and actually means a full citation in
          APA format, but it isn’t the same, and you can pick it out by how it functions in a
          sentence, if you’re ever confused.
        </Content>

        <Content>
          Lastly, sources are sometimes called “reference materials”, but rest assured that term is
          only interchangeable with “sources”and “source materials” and like terms that essentially
          mean “the thing you used information from”.
        </Content>

        <Content>
          However, you also have the option in Author-Date to title this page “Works Cited”, if you
          prefer; either title is correct if you’re working in that system of Chicago Style.
        </Content>
      </>
    ),
  },
];

const Basics = () => (
  <Grid
    container
    flexDirection="column"
    justifyContent="flex-start"
    alignItems="center"
    bgcolor={(theme) => theme.specialColors.CG_INFO_BG}
    px="16px"
    py="40px"
  >
    <Box maxWidth="1028px">
      <Typography component="h2" fontSize="20px" fontWeight="600" textAlign="left" pl="8px">
        Citation Basics
      </Typography>

      {basics.map((item) => (
        <FaqSectionAccorion
          key={item.question}
          question={item.question}
          questionComponent={item.questionComponent}
          answer={item.answer}
          hoverColor="#fff"
        />
      ))}
    </Box>
  </Grid>
);

export default Basics;
